const SEO_MARKER = 'data-seo-dynamic';

/**
 * Create or update a <meta> tag in <head>.
 */
export function updateMetaTag(
  attr: 'name' | 'property',
  key: string,
  content: string,
): void {
  let el = document.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    el.setAttribute(SEO_MARKER, '');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/**
 * Remove a <meta> tag from <head>.
 */
export function removeMetaTag(attr: 'name' | 'property', key: string): void {
  const el = document.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"][${SEO_MARKER}]`,
  );
  el?.remove();
}

/**
 * Set the document title.
 */
export function setDocumentTitle(title: string): void {
  document.title = title;
}

/**
 * Create or update <link rel="canonical"> in <head>.
 */
export function updateCanonicalUrl(url: string): void {
  let link = document.querySelector<HTMLLinkElement>(
    `link[rel="canonical"]`,
  );
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute(SEO_MARKER, '');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

export interface OgTags {
  title: string;
  description: string;
  url: string;
  image?: string;
  type?: string;
  locale?: string;
  siteName?: string;
}

/**
 * Batch update Open Graph meta tags.
 */
export function updateOgTags(tags: OgTags): void {
  updateMetaTag('property', 'og:title', tags.title);
  updateMetaTag('property', 'og:description', tags.description);
  updateMetaTag('property', 'og:url', tags.url);
  updateMetaTag('property', 'og:type', tags.type || 'website');
  updateMetaTag('property', 'og:locale', tags.locale || 'pl_PL');
  if (tags.image) {
    updateMetaTag('property', 'og:image', tags.image);
  }
  if (tags.siteName) {
    updateMetaTag('property', 'og:site_name', tags.siteName);
  }
}

const OG_PROPERTIES = [
  'og:title',
  'og:description',
  'og:url',
  'og:type',
  'og:locale',
  'og:image',
  'og:site_name',
];

/**
 * Remove all dynamically added SEO tags (meta + canonical).
 * Call on component unmount to clean up.
 */
export function clearPageSeo(): void {
  // Remove dynamically added meta tags
  document
    .querySelectorAll(`[${SEO_MARKER}]`)
    .forEach((el) => el.remove());
}
